import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui";
import { getErrorMessage } from "@/lib/strapi";
import { ROUTES } from "@/routes/paths";
import { CheckoutFields, checkoutSchema, checkoutInitialValues } from "@/features/orders";
import { useCreateCustomOrder } from "../hooks/useCreateCustomOrder";

const schema = checkoutSchema.shape({
  measurements: Yup.string().trim().max(2000, "Too long"),
  color_preference: Yup.string().trim().max(120, "Too long"),
  special_instructions: Yup.string().trim().max(2000, "Too long"),
  event_date: Yup.string(),
});

const inputClass =
  "w-full rounded-md border border-input bg-background p-2 text-foreground focus:border-ring focus:ring-1 focus:ring-ring";

/** A made-to-order request: measurements and preferences, plus contact details. */
export function CustomOrderForm() {
  const navigate = useNavigate();
  const create = useCreateCustomOrder({
    onSuccess: (order) => {
      toast.success(`Request ${order.order_number} sent — check your email.`);
      navigate(ROUTES.TRACK_CUSTOM(order.access_token));
    },
    onError: (error) => toast.error(getErrorMessage(error, "Could not send request")),
  });

  return (
    <Formik
      initialValues={{
        ...checkoutInitialValues,
        measurements: "",
        color_preference: "",
        special_instructions: "",
        event_date: "",
      }}
      validationSchema={schema}
      onSubmit={(values) => create.mutate(values)}
    >
      <Form className="space-y-5">
        <fieldset className="space-y-3">
          <legend className="mb-1 text-sm font-semibold text-foreground">Your request</legend>
          <div>
            <label htmlFor="event_date" className="mb-1 block text-sm text-foreground">
              Event date
            </label>
            <Field id="event_date" name="event_date" type="date" className={inputClass} />
          </div>
          <div>
            <label htmlFor="color_preference" className="mb-1 block text-sm text-foreground">
              Colour preference
            </label>
            <Field
              id="color_preference"
              name="color_preference"
              placeholder="Emerald green"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="measurements" className="mb-1 block text-sm text-foreground">
              Measurements
            </label>
            <Field
              id="measurements"
              name="measurements"
              as="textarea"
              rows="3"
              placeholder="Bust 36, Waist 30, Hips 38…"
              className={inputClass}
            />
            <ErrorMessage name="measurements" component="p" className="mt-1 text-xs text-destructive" />
          </div>
          <div>
            <label htmlFor="special_instructions" className="mb-1 block text-sm text-foreground">
              Special instructions
            </label>
            <Field
              id="special_instructions"
              name="special_instructions"
              as="textarea"
              rows="3"
              placeholder="Full sleeves, no embellishment on the back…"
              className={inputClass}
            />
          </div>
        </fieldset>

        <CheckoutFields showNotes={false} />

        <Button type="submit" className="w-full" isLoading={create.isPending}>
          Send request
        </Button>
      </Form>
    </Formik>
  );
}

export default CustomOrderForm;
